import { Vec2d, VecLike } from './Vec2d';
import { Point2d } from './Point2d';
import { GeometryUtils } from './geometry';

/**
 * 线段模型接口
 */
export interface LineSegmentModel {
  start: VecLike;
  end: VecLike;
}

/**
 * 线段类型
 */
export type LineSegmentLike = LineSegment | LineSegmentModel;

/**
 * 线段类
 * 表示2D平面中的一条线段，由起点和终点定义
 */
export class LineSegment {
  constructor(
    public start: Vec2d = new Vec2d(),
    public end: Vec2d = new Vec2d(),
  ) {}

  /**
   * 设置线段的起点和终点
   */
  set(start: VecLike, end: VecLike): LineSegment {
    this.start.setTo(Vec2d.Cast(start));
    this.end.setTo(Vec2d.Cast(end));
    return this;
  }

  /**
   * 设置起点
   */
  setStart(point: VecLike): LineSegment {
    this.start.setTo(Vec2d.Cast(point));
    return this;
  }

  /**
   * 设置终点
   */
  setEnd(point: VecLike): LineSegment {
    this.end.setTo(Vec2d.Cast(point));
    return this;
  }

  /**
   * 克隆线段
   */
  clone(): LineSegment {
    return new LineSegment(this.start.clone(), this.end.clone());
  }

  /**
   * 获取线段的长度
   */
  get length(): number {
    return GeometryUtils.distance(this.start, this.end);
  }

  /**
   * 获取线段的长度平方（用于性能优化）
   */
  get lengthSquared(): number {
    return Vec2d.Len2(Vec2d.Sub(this.end, this.start));
  }

  /**
   * 获取线段的中点
   */
  get midpoint(): Vec2d {
    return GeometryUtils.midpoint(this.start, this.end);
  }

  /**
   * 获取线段的方向向量（单位向量）
   */
  get direction(): Vec2d {
    return Vec2d.Tan(this.end, this.start);
  }

  /**
   * 获取线段的法向量（垂直于线段）
   */
  get normal(): Vec2d {
    return Vec2d.Per(this.direction);
  }

  /**
   * 获取线段的包围盒
   */
  get boundingBox(): { min: Vec2d; max: Vec2d } {
    return GeometryUtils.boundingBox([this.start, this.end]);
  }

  /**
   * 计算点到线段的距离
   */
  distanceToPoint(point: VecLike): number {
    return GeometryUtils.pointToLineDistance(point, this.toJson());
  }

  /**
   * 获取线段上距离指定点最近的点
   */
  nearestPointOnSegment(point: VecLike, clamp = true): Vec2d {
    return GeometryUtils.nearestPointOnLineSegment(this.start, this.end, point, clamp);
  }

  /**
   * 判断点是否在线段上（考虑容差）
   */
  containsPoint(point: VecLike, tolerance = 0.0001): boolean {
    const nearest = this.nearestPointOnSegment(point, true);
    return GeometryUtils.isNear(point, nearest, tolerance);
  }

  /**
   * 判断点是否在线段上（精确判断）
   */
  containsPointExact(point: VecLike): boolean {
    // 检查点是否在线段的包围盒内
    const { min, max } = this.boundingBox;
    if (point.x < min.x || point.x > max.x || point.y < min.y || point.y > max.y) {
      return false;
    }

    // 检查三点共线
    const area = Math.abs(
      (this.end.x - this.start.x) * (point.y - this.start.y) -
      (point.x - this.start.x) * (this.end.y - this.start.y),
    );
    
    return area < 1e-10;
  }

  /**
   * 计算与另一条线段的交点
   */
  intersection(other: LineSegmentLike): Vec2d | null {
    const otherSegment = other instanceof LineSegment ? other : new LineSegment(
      Vec2d.Cast(other.start),
      Vec2d.Cast(other.end),
    );
    
    return GeometryUtils.lineIntersection(
      this.start, this.end,
      otherSegment.start, otherSegment.end,
    );
  }

  /**
   * 判断是否与另一条线段相交
   */
  intersects(other: LineSegmentLike): boolean {
    return this.intersection(other) !== null;
  }

  /**
   * 判断是否与另一条线段平行
   */
  isParallel(other: LineSegmentLike): boolean {
    const otherSegment = other instanceof LineSegment ? other : new LineSegment(
      Vec2d.Cast(other.start),
      Vec2d.Cast(other.end),
    );
    
    const dir1 = this.direction;
    const dir2 = otherSegment.direction;
    
    const cross = Vec2d.Cpr(dir1, dir2);
    return Math.abs(cross) < 1e-10;
  }

  /**
   * 判断是否与另一条线段垂直
   */
  isPerpendicular(other: LineSegmentLike): boolean {
    const otherSegment = other instanceof LineSegment ? other : new LineSegment(
      Vec2d.Cast(other.start),
      Vec2d.Cast(other.end),
    );
    
    const dir1 = this.direction;
    const dir2 = otherSegment.direction;
    
    const dot = Vec2d.Dpr(dir1, dir2);
    return Math.abs(dot) < 1e-10;
  }

  /**
   * 计算与另一条线段的夹角（弧度）
   */
  angleTo(other: LineSegmentLike): number {
    const otherSegment = other instanceof LineSegment ? other : new LineSegment(
      Vec2d.Cast(other.start),
      Vec2d.Cast(other.end),
    );
    
    const dir1 = this.direction;
    const dir2 = otherSegment.direction;
    
    const dot = Vec2d.Dpr(dir1, dir2);
    const cross = Vec2d.Cpr(dir1, dir2);
    
    return Math.atan2(cross, dot);
  }

  /**
   * 计算与另一条线段的夹角（度数）
   */
  angleToDegrees(other: LineSegmentLike): number {
    return this.angleTo(other) * (180 / Math.PI);
  }

  /**
   * 平移线段
   */
  translate(offset: VecLike): LineSegment {
    this.start.add(offset);
    this.end.add(offset);
    return this;
  }

  /**
   * 缩放线段（以起点为中心）
   */
  scale(factor: number): LineSegment {
    const direction = this.direction;
    const length = this.length;
    this.end.setTo(this.start).add(direction.mul(length * factor));
    return this;
  }

  /**
   * 缩放线段（以指定点为中心）
   */
  scaleWithOrigin(factor: number, origin: VecLike): LineSegment {
    this.start.setTo(Vec2d.ScaleWithOrigin(this.start, factor, origin));
    this.end.setTo(Vec2d.ScaleWithOrigin(this.end, factor, origin));
    return this;
  }

  /**
   * 旋转线段（以起点为中心）
   */
  rotate(angle: number): LineSegment {
    this.end.rotWith(this.start, angle);
    return this;
  }

  /**
   * 旋转线段（以指定点为中心）
   * angle: 旋转角度
   * origin: 旋转中心点
   */
  rotateWithOrigin(angle: number, origin: VecLike): LineSegment {
    this.start.rotWith(origin, angle);
    this.end.rotWith(origin, angle);
    return this;
  }

  /**
   * 将线段分割成指定数量的等长段
   */
  divide(segments: number): Vec2d[] {
    if (segments <= 1) return [this.start.clone(), this.end.clone()];
    
    const points: Vec2d[] = [];
    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      points.push(Vec2d.Lrp(this.start, this.end, t));
    }
    
    return points;
  }

  /**
   * 将线段分割成指定长度的段
   */
  divideByLength(segmentLength: number): Vec2d[] {
    const totalLength = this.length;
    const segments = Math.floor(totalLength / segmentLength);
    
    if (segments <= 0) return [this.start.clone(), this.end.clone()];
    
    const points: Vec2d[] = [this.start.clone()];
    for (let i = 1; i <= segments; i++) {
      const t = (i * segmentLength) / totalLength;
      points.push(Vec2d.Lrp(this.start, this.end, t));
    }
    
    if (!GeometryUtils.isNear(points[points.length - 1], this.end)) {
      points.push(this.end.clone());
    }
    
    return points;
  }

  /**
   * 获取线段上的点（参数化）
   */
  getPointAt(t: number): Vec2d {
    return Vec2d.Lrp(this.start, this.end, Math.max(0, Math.min(1, t)));
  }

  /**
   * 判断两条线段是否相等
   */
  equals(other: LineSegmentLike, tolerance = 0.0001): boolean {
    const otherSegment = other instanceof LineSegment ? other : new LineSegment(
      Vec2d.Cast(other.start),
      Vec2d.Cast(other.end),
    );
    
    return (GeometryUtils.isNear(this.start, otherSegment.start, tolerance) &&
            GeometryUtils.isNear(this.end, otherSegment.end, tolerance)) ||
           (GeometryUtils.isNear(this.start, otherSegment.end, tolerance) &&
            GeometryUtils.isNear(this.end, otherSegment.start, tolerance));
  }

  /**
   * 转换为Point2d数组
   */
  toPoint2dArray(): Point2d[] {
    return [
      new Point2d(this.start.x, this.start.y),
      new Point2d(this.end.x, this.end.y),
    ];
  }

  /**
   * 转换为Vec2d数组
   */
  toVec2dArray(): Vec2d[] {
    return [this.start.clone(), this.end.clone()];
  }

  /**
   * 转换为JSON对象
   */
  toJson(): LineSegmentModel {
    return {
      start: this.start.toJson(),
      end: this.end.toJson(),
    };
  }

  /**
   * 转换为字符串
   */
  toString(): string {
    return `LineSegment(${this.start.toString()} -> ${this.end.toString()})`;
  }

  /**
   * 静态方法：从两个点创建线段
   */
  static fromPoints(start: VecLike, end: VecLike): LineSegment {
    return new LineSegment(Vec2d.Cast(start), Vec2d.Cast(end));
  }

  /**
   * 静态方法：从Point2d数组创建线段
   */
  static fromPoint2dArray(points: Point2d[]): LineSegment {
    if (points.length < 2) {
      throw new Error('至少需要两个点来创建线段');
    }
    return new LineSegment(
      new Vec2d(points[0].x, points[0].y),
      new Vec2d(points[points.length - 1].x, points[points.length - 1].y),
    );
  }

  /**
   * 静态方法：从JSON对象创建线段
   */
  static fromJson(json: LineSegmentModel): LineSegment {
    return new LineSegment(
      Vec2d.From(json.start),
      Vec2d.From(json.end),
    );
  }

  /**
   * 静态方法：计算两条线段的交点
   */
  static intersection(seg1: LineSegmentLike, seg2: LineSegmentLike): Vec2d | null {
    const line1 = seg1 instanceof LineSegment ? seg1 : LineSegment.fromJson(seg1);
    const line2 = seg2 instanceof LineSegment ? seg2 : LineSegment.fromJson(seg2);
    
    return line1.intersection(line2);
  }

  /**
   * 静态方法：判断两条线段是否相交
   */
  static intersects(seg1: LineSegmentLike, seg2: LineSegmentLike): boolean {
    const line1 = seg1 instanceof LineSegment ? seg1 : LineSegment.fromJson(seg1);
    const line2 = seg2 instanceof LineSegment ? seg2 : LineSegment.fromJson(seg2);
    
    return line1.intersects(line2);
  }

  /**
   * 静态方法：计算点到线段的距离
   */
  static distanceToPoint(segment: LineSegmentLike, point: VecLike): number {
    const line = segment instanceof LineSegment ? segment : LineSegment.fromJson(segment);
    return GeometryUtils.pointToLineDistance(point, line.toJson());
  }
}
